import React, { useState, useEffect } from 'react';
import DataTable from "../ui/DataTable";

const RegisterView = ({
                          activeTab,
                          filters,
                          onFilterChange,
                          onApplyFilters,
                          onResetFilters,
                          niraPage,
                          niraLoading,
                          niraError,
                          niraSize,
                          onViewNiraRequest,
                          onPageChange,
                          onPageSizeChange,
                          // Stanbic props
                          stanbicData = [],
                          stanbicLoading = false,
                          stanbicError = null,
                          onViewStanbicBatch,
                          stanbicBreakdownData = [],
                          stanbicBreakdownLoading = false,
                          stanbicPaymentResponses = [],
                          selectedStanbicBatchId = null,
                          onCloseStanbicBreakdown,
                          // Batch props
                          batchData = [],
                          batchLoading = false,
                          batchError = null,
                          onViewBatchBreakdown,
                          batchBreakdownData = [],
                          batchBreakdownLoading = false,
                          selectedBatchId = null,
                          onCloseBreakdown
                      }) => {
    const [localSelectedBatchId, setLocalSelectedBatchId] = useState(selectedStanbicBatchId || selectedBatchId);
    const [showDetailedView, setShowDetailedView] = useState(false);
    const [detailedBatch, setDetailedBatch] = useState(null);

    useEffect(() => {
        setLocalSelectedBatchId(selectedStanbicBatchId || selectedBatchId);
    }, [selectedStanbicBatchId, selectedBatchId]);

    const handleViewStanbicBatch = (batchId, batchData) => {
        setLocalSelectedBatchId(batchId);
        onViewStanbicBatch(batchId);
    };

    const handleCloseStanbicBreakdown = () => {
        setLocalSelectedBatchId(null);
        onCloseStanbicBreakdown();
    };

    const handleRowDoubleClick = (batch) => {
        setDetailedBatch(batch);
        setShowDetailedView(true);
    };

    const handleCloseDetailedView = () => {
        setShowDetailedView(false);
        setDetailedBatch(null);
    };

    // Format currency display
    const formatCurrency = (amount, currencyCode = 'UGX') => {
        if (!amount) return '-';
        return `${currencyCode} ${parseFloat(amount).toLocaleString()}`;
    };

    // Format date display
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString();
    };

    // Format date with time
    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString();
    };

    // Get status badge class
    const getStatusBadgeClass = (status) => {
        if (!status) return '';
        const statusLower = status.toLowerCase();
        if (statusLower.includes('verified') || statusLower.includes('processed') || statusLower.includes('success') || statusLower.includes('approved')) {
            return 'verified';
        } else if (statusLower.includes('failed') || statusLower.includes('error') || statusLower.includes('rejected')) {
            return 'failed';
        } else if (statusLower.includes('pending') || statusLower.includes('submitted')) {
            return 'pending';
        } else if (statusLower.includes('recalled')) {
            return 'recalled';
        }
        return '';
    };

    // Stanbic batch columns
    const getStanbicColumns = () => {
        const allColumns = [
            {
                header: 'Batch ID',
                accessorFn: (row) => row.id,
                id: 'id',
                hidden: true,
                cell: (info) => info.getValue(),
            },
            {
                header: 'Batch Date',
                accessorFn: (row) => (row.batchDate),
                id: 'batchDate',
                cell: (info) => info.getValue(),
            },
            {
                header: 'Value Date',
                accessorFn: (row) => (row.valueDate),
                id: 'valueDate',
                cell: (info) => info.getValue(),
            },
            {
                header: 'Batch Title',
                accessorFn: (row) => row.batchTitle || '-',
                id: 'batchTitle',
                cell: (info) => info.getValue(),
            },
            {
                header: 'Total SC',
                accessorFn: (row) => formatCurrency(row.totalSourceCurrency, row.transactionCurrency),
                id: 'totalSourceCurrency',
                cell: (info) => info.getValue(),
            },
            {
                header: 'Total BC',
                accessorFn: (row) => formatCurrency(row.totalBaseCurrency),
                id: 'totalBaseCurrency',
                cell: (info) => info.getValue(),
            },
            {
                header: 'Cashbook Name',
                accessorFn: (row) => row.cashbookName || '-',
                id: 'cashbookName',
                cell: (info) => info.getValue(),
            },
            {
                header: 'Cashbook Account',
                accessorFn: (row) => row.cashbookAccountNo || '-',
                id: 'cashbookAccountNo',
                hidden: true,
                cell: (info) => info.getValue(),
            },
            {
                header: 'Payment Status',
                accessorFn: (row) => row.paymentStatus || '-',
                id: 'paymentStatus',
                cell: (info) => {
                    const val = info.getValue();
                    const cls = getStatusBadgeClass(val);
                    return <span className={`status-badge ${cls}`}>{String(val)}</span>;
                },
            },
            {
                header: 'Status Date',
                accessorFn: (row) => formatDateTime(row.statusDate),
                id: 'statusDate',
                hidden: true,
                cell: (info) => info.getValue(),
            },
            {
                header: 'Response Type',
                accessorFn: (row) => row.responseType || '-',
                id: 'responseType',
                cell: (info) => info.getValue(),
            },
            {
                header: 'Response Date',
                accessorFn: (row) => (row.responseDate),
                id: 'responseDate',
                hidden: true,
                cell: (info) => info.getValue(),
            },
            {
                header: 'Status Description',
                accessorFn: (row) => row.statusDescription || '-',
                id: 'statusDescription',
                hidden: true,
                cell: (info) => {
                    const val = info.getValue();
                    return <span className="status-description" title={val}>{val.length > 30 ? val.substring(0, 30) + '...' : val}</span>;
                },
            },
            {
                header: 'Actions',
                id: 'actions',
                cell: ({ row }) => (
                    <div className="action-buttons">
                        <button
                            className="btn-sm btn-primary"
                            onClick={() => handleViewStanbicBatch(row.original.id, row.original)}
                        >
                            <i className="fas fa-eye"></i> View
                        </button>
                        <button
                            className="btn-sm btn-info"
                            onClick={() => handleRowDoubleClick(row.original)}
                            style={{ marginLeft: '5px' }}
                        >
                            <i className="fas fa-info-circle"></i> Details
                        </button>
                    </div>
                ),
            }
        ];

        // Filter out hidden columns
        return allColumns.filter(column => !column.hidden);
    };

    // Batch Detail Modal Component
    const BatchDetailModal = ({ batch, onClose }) => {
        if (!batch) return null;

        return (
            <div className="modal-overlay">
                <div className="modal-container">
                    <div className="modal-header">
                        <h3>Batch Details - ID: {batch.id}</h3>
                        <button className="btn-close" onClick={onClose}>
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                    <div className="modal-body">
                        <div className="details-grid">
                            <div className="detail-section">
                                <h4>Basic Information</h4>
                                <div className="detail-row">
                                    <span className="detail-label">Batch Title:</span>
                                    <span className="detail-value">{batch.batchTitle || '-'}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Batch Date:</span>
                                    <span className="detail-value">{formatDate(batch.batchDate)}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Value Date:</span>
                                    <span className="detail-value">{formatDate(batch.valueDate)}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Scheme Name:</span>
                                    <span className="detail-value">{batch.schemeName || '-'}</span>
                                </div>
                            </div>

                            <div className="detail-section">
                                <h4>Cashbook Information</h4>
                                <div className="detail-row">
                                    <span className="detail-label">Cashbook Name:</span>
                                    <span className="detail-value">{batch.cashbookName || '-'}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Account Number:</span>
                                    <span className="detail-value">{batch.cashbookAccountNo || '-'}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Account Name:</span>
                                    <span className="detail-value">{batch.cashbookAccountName || '-'}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Branch Code:</span>
                                    <span className="detail-value">{batch.cashbookBranchCode || '-'}</span>
                                </div>
                            </div>

                            <div className="detail-section">
                                <h4>Financial Details</h4>
                                <div className="detail-row">
                                    <span className="detail-label">Total Source Currency:</span>
                                    <span className="detail-value">{formatCurrency(batch.totalSourceCurrency, batch.cashbookCurrencyCode)}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Total Base Currency:</span>
                                    <span className="detail-value">{formatCurrency(batch.totalBaseCurrency)}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Number of Lines:</span>
                                    <span className="detail-value">{batch.countLines || 0}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Currency Code:</span>
                                    <span className="detail-value">{batch.baseCurrencyCode || '-'}</span>
                                </div>
                            </div>

                            <div className="detail-section">
                                <h4>Payment Information</h4>
                                <div className="detail-row">
                                    <span className="detail-label">Payment Status:</span>
                                    <span className={`detail-value status-badge ${getStatusBadgeClass(batch.paymentStatus)}`}>
                                        {batch.paymentStatus || '-'}
                                    </span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Status Date:</span>
                                    <span className="detail-value">{formatDateTime(batch.statusDate)}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Response Type:</span>
                                    <span className="detail-value">{batch.responseType || '-'}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Response Date:</span>
                                    <span className="detail-value">{formatDateTime(batch.responseDate)}</span>
                                </div>
                            </div>

                            <div className="detail-section full-width">
                                <h4>Additional Details</h4>
                                <div className="detail-row">
                                    <span className="detail-label">Prepared By:</span>
                                    <span className="detail-value">{batch.preparedByName || '-'}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Message ID:</span>
                                    <span className="detail-value">{batch.messageId || '-'}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">File Name:</span>
                                    <span className="detail-value">{batch.fileNameNative || '-'}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Status Description:</span>
                                    <span className="detail-value">{batch.statusDescription || '-'}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button className="btn-secondary" onClick={onClose}>
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="register-view">
            <h2>Transaction Register</h2>

            {/* Filter Section */}
            <div className="filter-section">
                <div className="filter-grid">
                    <div className="filter-group">
                        <label>Date From</label>
                        <input
                            type="date"
                            name="dateFrom"
                            value={filters.dateFrom}
                            onChange={onFilterChange}
                        />
                    </div>
                    <div className="filter-group">
                        <label>Date To</label>
                        <input
                            type="date"
                            name="dateTo"
                            value={filters.dateTo}
                            onChange={onFilterChange}
                        />
                    </div>
                    <div className="filter-group">
                        <label>Search</label>
                        <input
                            type="text"
                            name="searchTerm"
                            placeholder={activeTab === 'batch' || activeTab === 'stanbic' ? "Search batch title, scheme name..." : "Search..."}
                            value={filters.searchTerm}
                            onChange={onFilterChange}
                        />
                    </div>
                    {(activeTab === 'batch' || activeTab === 'stanbic') && (
                        <div className="filter-group">
                            <label>Cashbook Code</label>
                            <input
                                type="text"
                                name="cashbookCode"
                                placeholder="Cashbook code..."
                                value={filters.cashbookCode}
                                onChange={onFilterChange}
                            />
                        </div>
                    )}
                    <div className="filter-group">
                        <label>Status</label>
                        <select
                            name="status"
                            value={filters.status}
                            onChange={onFilterChange}
                        >
                            <option value="">All Statuses</option>
                            {activeTab === 'nira' ? (
                                <>
                                    <option value="verified">Verified</option>
                                    <option value="pending">Pending</option>
                                    <option value="failed">Failed</option>
                                </>
                            ) : (activeTab === 'batch' || activeTab === 'stanbic') ? (
                                <>
                                    <option value="processed">Processed</option>
                                    <option value="pending">Pending</option>
                                    <option value="failed">Failed</option>
                                    <option value="approved">Approved</option>
                                    <option value="rejected">Rejected</option>
                                    <option value="recalled">Recalled</option>
                                </>
                            ) : (
                                <>
                                    <option value="Processed">Processed</option>
                                    <option value="Failed">Failed</option>
                                    <option value="Pending">Pending</option>
                                </>
                            )}
                        </select>
                    </div>
                </div>
                <div className="filter-actions">
                    <button className="btn-primary" onClick={onApplyFilters}>
                        <i className="fas fa-filter"></i> Apply Filters
                    </button>
                    <button className="btn-secondary" onClick={onResetFilters}>
                        <i className="fas fa-redo"></i> Reset
                    </button>
                </div>
            </div>

            {/* Stanbic Tab */}
            {activeTab === 'stanbic' && (
                <div className="table-container">
                    {stanbicError && (
                        <div className="error-message" style={{ padding: 12, color: 'var(--pps-danger)' }}>
                            Error: {stanbicError}
                        </div>
                    )}

                    {localSelectedBatchId ? (
                        // Stanbic Breakdown View
                        <div className="batch-breakdown">
                            <div className="breakdown-header">
                                <button
                                    className="btn-secondary btn-sm"
                                    onClick={handleCloseStanbicBreakdown}
                                    style={{ marginBottom: '1rem' }}
                                >
                                    <i className="fas fa-arrow-left"></i> Back to Stanbic List
                                </button>
                                <h3>Stanbic Batch Breakdown - ID: {localSelectedBatchId}</h3>
                            </div>

                            {/* Tabs for different breakdown views */}
                            <div className="breakdown-tabs">
                                <div className="tab-buttons">
                                    <button className="tab-button active">Batch Lines</button>
                                    <button className="tab-button">Payment Responses</button>
                                    <button className="tab-button">Batch Details</button>
                                </div>

                                {/* Batch Lines Tab Content */}
                                <div className="tab-content active">
                                    {stanbicBreakdownLoading ? (
                                        <div className="loading" style={{ textAlign: 'center', padding: '2rem' }}>
                                            <i className="fas fa-spinner fa-spin"></i> Loading batch lines...
                                        </div>
                                    ) : (
                                        <DataTable
                                            columns={[
                                                {
                                                    header: 'Line ID',
                                                    accessorFn: (row) => row.id || '-',
                                                    id: 'lineId',
                                                    cell: (info) => info.getValue(),
                                                },
                                                {
                                                    header: 'Payment ID',
                                                    accessorFn: (row) => row.paymentId || '-',
                                                    id: 'paymentId',
                                                    cell: (info) => info.getValue(),
                                                },
                                                {
                                                    header: 'Customer Ref',
                                                    accessorFn: (row) => row.customerRef || '-',
                                                    id: 'customerRef',
                                                    cell: (info) => info.getValue(),
                                                },
                                                {
                                                    header: 'Beneficiary Name',
                                                    accessorFn: (row) => row.beneficiaryName || '-',
                                                    id: 'beneficiaryName',
                                                    cell: (info) => info.getValue(),
                                                },
                                                {
                                                    header: 'Account No',
                                                    accessorFn: (row) => row.accountNo || '-',
                                                    id: 'accountNo',
                                                    cell: (info) => info.getValue(),
                                                },
                                                {
                                                    header: 'Bank Name',
                                                    accessorFn: (row) => row.bankName || '-',
                                                    id: 'bankName',
                                                    cell: (info) => info.getValue(),
                                                },
                                                {
                                                    header: 'Amount (SC)',
                                                    accessorFn: (row) => formatCurrency(row.amountSc, row.paymentCurrencyCode),
                                                    id: 'amountSc',
                                                    cell: (info) => info.getValue(),
                                                },
                                                {
                                                    header: 'Amount (BC)',
                                                    accessorFn: (row) => formatCurrency(row.amountBc),
                                                    id: 'amountBc',
                                                    cell: (info) => info.getValue(),
                                                },
                                                {
                                                    header: 'Purpose',
                                                    accessorFn: (row) => row.purposeOfPayment || '-',
                                                    id: 'purpose',
                                                    cell: (info) => info.getValue(),
                                                },
                                                {
                                                    header: 'Status',
                                                    accessorFn: (row) => row.status || 'Processed',
                                                    id: 'status',
                                                    cell: (info) => {
                                                        const val = info.getValue();
                                                        const cls = getStatusBadgeClass(val);
                                                        return <span className={`status-badge ${cls}`}>{String(val)}</span>;
                                                    },
                                                }
                                            ]}
                                            data={stanbicBreakdownData}
                                            loading={stanbicBreakdownLoading}
                                            emptyMessage="No batch lines found for this batch"
                                            pageSize={10}
                                        />
                                    )}
                                </div>

                                {/* Payment Responses Tab Content */}
                                <div className="tab-content">
                                    {stanbicPaymentResponses.length === 0 ? (
                                        <div className="empty-message">No payment responses available</div>
                                    ) : (
                                        <DataTable
                                            columns={[
                                                {
                                                    header: 'Response ID',
                                                    accessorFn: (row) => row.id || '-',
                                                    id: 'responseId',
                                                    cell: (info) => info.getValue(),
                                                },
                                                {
                                                    header: 'Response Type',
                                                    accessorFn: (row) => row.responseType || '-',
                                                    id: 'responseType',
                                                    cell: (info) => info.getValue(),
                                                },
                                                {
                                                    header: 'Payment Status',
                                                    accessorFn: (row) => row.paymentStatus || '-',
                                                    id: 'paymentStatus',
                                                    cell: (info) => {
                                                        const val = info.getValue();
                                                        const cls = getStatusBadgeClass(val);
                                                        return <span className={`status-badge ${cls}`}>{String(val)}</span>;
                                                    },
                                                },
                                                {
                                                    header: 'Status Description',
                                                    accessorFn: (row) => row.statusDescription || '-',
                                                    id: 'statusDescription',
                                                    cell: (info) => info.getValue(),
                                                },
                                                {
                                                    header: 'Response Date',
                                                    accessorFn: (row) => formatDateTime(row.responseDate),
                                                    id: 'responseDate',
                                                    cell: (info) => info.getValue(),
                                                },
                                                {
                                                    header: 'Date Created',
                                                    accessorFn: (row) => formatDateTime(row.dateCreated),
                                                    id: 'dateCreated',
                                                    cell: (info) => info.getValue(),
                                                },
                                                {
                                                    header: 'File Name',
                                                    accessorFn: (row) => row.fileName || '-',
                                                    id: 'fileName',
                                                    cell: (info) => info.getValue(),
                                                },
                                                {
                                                    header: 'Raw Response',
                                                    accessorFn: (row) => row.rawResponse || '-',
                                                    id: 'rawResponse',
                                                    cell: (info) => {
                                                        const val = info.getValue();
                                                        return (
                                                            <button
                                                                className="btn-sm btn-info"
                                                                onClick={() => {
                                                                    alert(val);
                                                                }}
                                                            >
                                                                View Raw
                                                            </button>
                                                        );
                                                    },
                                                }
                                            ]}
                                            data={stanbicPaymentResponses}
                                            emptyMessage="No payment responses available"
                                            pageSize={10}
                                        />
                                    )}
                                </div>

                                {/* Batch Details Tab Content */}
                                <div className="tab-content">
                                    <div className="batch-details-card">
                                        <h4>Batch Information</h4>
                                        {stanbicData.find(b => b.id === localSelectedBatchId) && (
                                            <div className="details-list">
                                                <div className="detail-item">
                                                    <strong>Batch ID:</strong> {localSelectedBatchId}
                                                </div>
                                                <div className="detail-item">
                                                    <strong>Batch Title:</strong> {stanbicData.find(b => b.id === localSelectedBatchId).batchTitle}
                                                </div>
                                                <div className="detail-item">
                                                    <strong>Scheme:</strong> {stanbicData.find(b => b.id === localSelectedBatchId).schemeName}
                                                </div>
                                                <div className="detail-item">
                                                    <strong>Total Lines:</strong> {stanbicData.find(b => b.id === localSelectedBatchId).countLines}
                                                </div>
                                                <div className="detail-item">
                                                    <strong>Total Amount:</strong> {formatCurrency(stanbicData.find(b => b.id === localSelectedBatchId).totalBaseCurrency)}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        // Stanbic List View
                        <DataTable
                            columns={getStanbicColumns()}
                            data={stanbicData}
                            loading={stanbicLoading}
                            emptyMessage="No Stanbic batch records found"
                            pageIndex={0}
                            pageSize={100}
                            totalElements={stanbicData.length}
                            totalPages={Math.ceil(stanbicData.length / 100)}
                            onRowDoubleClick={handleRowDoubleClick}
                        />
                    )}
                </div>
            )}

            {/* NIRA Tab */}
            {activeTab === 'nira' && (
                <div className="table-container">
                    {niraError && (
                        <div style={{ padding: 12, color: 'var(--pps-danger)' }}>Error: {niraError}</div>
                    )}
                    <DataTable
                        columns={[
                            {
                                header: '#',
                                accessorFn: (row) => row.id,
                                id: 'id',
                                enableHiding: true,
                                visible: false,
                                cell: (info) => info.getValue(),
                            },{
                                header: 'NIN',
                                accessorFn: (row) => row.nationalId || '-',
                                id: 'nin',
                                cell: (info) => info.getValue(),
                            },{
                                header: 'Document ID',
                                accessorFn: (row) => row.documentId || '-',
                                id: 'documentId',
                                cell: (info) => info.getValue(),
                            },
                            {
                                header: 'Surname',
                                accessorFn: (row) => row.surname || '-',
                                id: 'surname',
                                cell: (info) => info.getValue(),
                            },{
                                header: 'Given Name',
                                accessorFn: (row) => row.givenNames || '-',
                                id: 'givenNames',
                                cell: (info) => info.getValue(),
                            },{
                                header: 'Other Name',
                                accessorFn: (row) => row.otherNames || '-',
                                id: 'otherNames',
                                cell: (info) => info.getValue(),
                            },
                            {
                                header: 'Status',
                                accessorFn: (row) => row.verificationStatus || '-',
                                id: 'status',
                                cell: (info) => {
                                    const val = info.getValue();
                                    const cls = getStatusBadgeClass(val);
                                    return <span className={`status-badge ${cls}`}>{String(val)}</span>;
                                },
                            },
                            {
                                header: 'Date/Time',
                                accessorFn: (row) => row.dateCreated || row.dateUpdated || '-',
                                id: 'date',
                                cell: (info) => String(info.getValue()),
                            },
                            {
                                header: 'Actions',
                                id: 'actions',
                                cell: ({ row }) => (
                                    <button className="btn-sm" onClick={() => onViewNiraRequest(row.original?.id)}>
                                        <i className="fas fa-eye"></i> View
                                    </button>
                                ),
                            },
                        ]}
                        data={niraPage.content || []}
                        loading={niraLoading}
                        emptyMessage="No records found"
                        pageIndex={niraPage.number}
                        pageSize={niraPage.size || niraSize}
                        totalElements={niraPage.totalElements}
                        totalPages={niraPage.totalPages}
                        onPageChange={onPageChange}
                        onPageSizeChange={onPageSizeChange}
                    />
                </div>
            )}

            {/* Batch Tab */}
            {activeTab === 'batch' && (
                <div className="table-container">
                    {batchError && (
                        <div className="error-message" style={{ padding: 12, color: 'var(--pps-danger)' }}>
                            Error: {batchError}
                        </div>
                    )}

                    {localSelectedBatchId ? (
                        // Batch Breakdown View
                        <div className="batch-breakdown">
                            <div className="breakdown-header">
                                <button
                                    className="btn-secondary btn-sm"
                                    onClick={onCloseBreakdown}
                                    style={{ marginBottom: '1rem' }}
                                >
                                    <i className="fas fa-arrow-left"></i> Back to Batch List
                                </button>
                                <h3>Batch Breakdown - ID: {localSelectedBatchId}</h3>
                            </div>

                            {batchBreakdownLoading ? (
                                <div className="loading" style={{ textAlign: 'center', padding: '2rem' }}>
                                    <i className="fas fa-spinner fa-spin"></i> Loading breakdown...
                                </div>
                            ) : (
                                <DataTable
                                    columns={[
                                        {
                                            header: 'Payment ID',
                                            accessorFn: (row) => row.paymentId || '-',
                                            id: 'paymentId',
                                            cell: (info) => info.getValue(),
                                        },
                                        {
                                            header: 'Customer Ref',
                                            accessorFn: (row) => row.customerRef || '-',
                                            id: 'customerRef',
                                            cell: (info) => info.getValue(),
                                        },
                                        {
                                            header: 'Beneficiary Name',
                                            accessorFn: (row) => row.beneficiaryName || '-',
                                            id: 'beneficiaryName',
                                            cell: (info) => info.getValue(),
                                        },
                                        {
                                            header: 'Account No',
                                            accessorFn: (row) => row.accountNo || '-',
                                            id: 'accountNo',
                                            cell: (info) => info.getValue(),
                                        },
                                        {
                                            header: 'Bank Name',
                                            accessorFn: (row) => row.bankName || '-',
                                            id: 'bankName',
                                            cell: (info) => info.getValue(),
                                        },
                                        {
                                            header: 'Amount (SC)',
                                            accessorFn: (row) => formatCurrency(row.amountSc, row.paymentCurrencyCode),
                                            id: 'amountSc',
                                            cell: (info) => info.getValue(),
                                        },
                                        {
                                            header: 'Amount (BC)',
                                            accessorFn: (row) => formatCurrency(row.amountBc),
                                            id: 'amountBc',
                                            cell: (info) => info.getValue(),
                                        },
                                        {
                                            header: 'Purpose',
                                            accessorFn: (row) => row.purposeOfPayment || '-',
                                            id: 'purpose',
                                            cell: (info) => info.getValue(),
                                        },
                                        {
                                            header: 'Status',
                                            accessorFn: (row) => row.status || 'Processed',
                                            id: 'status',
                                            cell: (info) => {
                                                const val = info.getValue();
                                                const cls = getStatusBadgeClass(val);
                                                return <span className={`status-badge ${cls}`}>{String(val)}</span>;
                                            },
                                        }
                                    ]}
                                    data={batchBreakdownData}
                                    loading={batchBreakdownLoading}
                                    emptyMessage="No breakdown data found for this batch"
                                    pageSize={10}
                                />
                            )}
                        </div>
                    ) : (
                        // Batch List View
                        <DataTable
                            columns={getStanbicColumns().filter(col =>
                                !['cashbookAccountNo', 'paymentStatus', 'statusDate', 'responseType', 'responseDate', 'statusDescription'].includes(col.id)
                            )}
                            data={batchData}
                            loading={batchLoading}
                            emptyMessage="No batch records found"
                            pageIndex={0}
                            pageSize={100}
                            totalElements={batchData.length}
                            totalPages={Math.ceil(batchData.length / 100)}
                            onRowDoubleClick={handleRowDoubleClick}
                        />
                    )}
                </div>
            )}

            {/* SCB Tab */}
            {activeTab === 'scb' && (
                <div className="table-container">
                    <DataTable
                        columns={getStanbicColumns().filter(col =>
                            !['preparedBy', 'cashbook'].includes(col.id)
                        )}
                        data={batchData}
                        loading={batchLoading}
                        emptyMessage="No SCB batch records found"
                        pageIndex={0}
                        pageSize={10}
                        totalElements={batchData.length}
                        totalPages={Math.ceil(batchData.length / 10)}
                        onRowDoubleClick={handleRowDoubleClick}
                    />
                </div>
            )}

            {/* Detailed View Modal */}
            {showDetailedView && (
                <BatchDetailModal
                    batch={detailedBatch}
                    onClose={handleCloseDetailedView}
                />
            )}
        </div>
    );
};

export default RegisterView;